import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ClassType } from 'src/modules/utils/shared/class-type';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { EntityDefinition } from '../../models/entity-definition.model';
import { RestEndpoint } from '../../models/rest-endpoint.model';
import { FrontendForm } from '../../netgen/FrontendForm';
import { ButtonDefinition } from '../../models/button-definition.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Objects } from 'src/modules/utils/shared/objects';

@Component({
    selector: 'app-admin-base-edit',
    templateUrl: './admin-base-edit.component.html',
    styleUrl: './admin-base-edit.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AdminBaseEditComponent<T> extends ComponentView {

    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id: number;

    multiInsert = false;
    definition: EntityDefinition<T>;
    item: T;
    form: FrontendForm;

    async initParams(): Promise<boolean> {
        this.item ??= this.isInsert() ? (Objects.customInitialize(new this.definition.type() as object) as T)
            : await this.app.loadDataHandler(async () => this.getData(), this);
        await this.initAdditionalData();

        this.form = this.getForm();
        this.form.validateOnChange = true;
        await this.fillForm();
        this.form.validate();

        this.updateNavigation(null, [
            { label: this.app.t("general.administration"), routerLink: ["/base2", "admin"] },
            this.getBreadcrumbItemParent(),
            this.getBreadcrumbItem()
        ]);

        GuiUtils.angularTimer(() => this.form.fields[0].focus());

        return true;
    }

    async getData(): Promise<T> {
        return await RestEndpoint.main().findById(this.id).run(this.definition.apiEndpoint).get(this.definition.type as ClassType<object>) as T;
    }

    async upsert(item: T, buttonId?: string): Promise<boolean> {
        let result =(await this.app.saveDataHandler(async () => RestEndpoint.main().upsert(item).run(this.definition.apiEndpoint).getText(), this, true, !this.multiInsert))[0];

        await this.app.trackChanges();

        return result;
    }

    async initAdditionalData(): Promise<void> {
    }

    async fillForm(): Promise<void> {
        this.form.fill(this.item);
        await Promise.resolve(null);
    }

    async fromForm(): Promise<void> {
        this.form.get(this.item);
        await Promise.resolve(null);
    }

    getDialogButtons(): ButtonDefinition[] {
        return [
            new ButtonDefinition("save", this.app.t("general.save"), true, "fas fa-save", "", "success"),
            new ButtonDefinition("cancel", this.app.t("general.cancel"), true, "fas fa-times", "", "secondary")
        ];
    }

    async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "save") {
            await this.save(id);
        }
        else if (id == "cancel") {
            await this.close();
        }
    }

    getForm(): FrontendForm {
        return new FrontendForm([]);
    }

    getBreadcrumbItem(): MenuItem {
        return { label: this.definition.getName(), routerLink: this.definition.getAdminEditUrl() + "/" + this.id };
    }

    getBreadcrumbItemParent(): MenuItem {
        return { label: this.definition.getNamePlural(), routerLink: this.definition.adminListUrl };
    }

    isInsert(): boolean {
        return this.id == 0;
    }

    validateForm(buttonId?: string): boolean {
        return this.app.validateForm(this.form);
    }

    async save(buttonId?: string): Promise<void> {
        if (!this.validateForm(buttonId)) {
            return;
        }

        await this.fromForm();

        let ok = await this.upsert(this.item, buttonId);
        if (this.multiInsert && ok) {
            this.item = null;
            await this.initParams();
        }
    }

    async saveNew(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        await this.fromForm();
        (this.item as any).id = 0;
        let ok = await this.upsert(this.item);
        if (this.multiInsert && ok) {
            this.item = null;
            await this.initParams();
        }
    }
}
